import React, { Component } from 'react'
import { func, shape, string, object } from 'prop-types'
import { view } from '@risingstack/react-easy-state'
import { withRouter } from 'react-router'
import { translate } from 'react-i18next'
import { Gardeners } from '../../components/gardeners/Gardeners'
import { SubHero } from '../../components/header/SubHero'
import { stores, initialization } from '../../stores'

import { Loader } from '../../components/ui/states/Loader'
import { NoGardeners } from '../../components/ui/states/NoGardeners'
import { PagesPaths } from '../../constants/Pages'
import { LeadFunnels } from '../../constants/LeadFunnels'
import { updateCanonicalLink, injectSEOMetadata, removeCanonicalLink, addNoIndexIfNotExists, removeNoIndexIfExists, injectJsonLd, removeJsonLd, injectOpenGraph, removeOpenGraph } from '../../utilities/window'
import { buildListingJsonLd } from '../../utilities/gardener-seo'
import { debug } from '../../utilities/logger'
import { isSponsorCode } from '../../utilities/validators'

class GardenersPageComponent extends Component {
  async componentDidMount () {
    const { match, history } = this.props

    // Strict parameter validation
    const { city, code } = match.params

    if (!city) {
      debug('[GardenersPage] Missing city parameter, redirecting to home')
      history.replace('/')
      return
    }

    if (isSponsorCode(city)) {
      debug(`[GardenersPage] Sponsor code detected: ${city}`)
      await this.handleSponsorCode(city)
      return
    }

    const normalizedCity = this.normalizeCity(city)
    debug(
      `[GardenersPage] Original city: "${city}", Normalized: "${normalizedCity}"`
    )

    stores.inquiry.clear()
    await initialization

    if (code) {
      // PAGE WITH SPECIFIC POSTAL CODE (existing behavior)
      await this.handlePageWithPostalCode(normalizedCity, code)
    } else {
      // PAGE WITHOUT POSTAL CODE — always redirects to first CP, stop here
      await this.handlePageWithoutPostalCode(normalizedCity)
      return
    }

    // Pre-fill the inquiry information with account information
    // (connected can be null if the account could not be loaded at startup)
    if (stores.authentication.isAuthententicated() && stores.account.connected) {
      stores.inquiry.setAccount({ account: stores.account.connected })
    }

    stores.search.fromJardiniers = true
    stores.search.setEntryFunnel(LeadFunnels.SITE_CARTE)
    stores.search.selectAll()

    // Optimized SEO injection with validation
    await this.injectOptimizedSEO()

    // JSON-LD: BreadcrumbList + ItemList
    var listingJsonLd = buildListingJsonLd(
      stores.search.search.city,
      stores.search.search.postalCode,
      stores.search.gardeners
    )
    if (listingJsonLd) {
      injectJsonLd(listingJsonLd)
    }

    // Open Graph + Twitter Card
    var ogCity = stores.search.search.city
    var ogCode = stores.search.search.postalCode
    var ogPic = stores.search.pictureList && stores.search.pictureList[0]
      ? stores.search.pictureList[0].file.path
      : undefined
    injectOpenGraph({
      title: 'Jardiniers paysagistes à ' + ogCity + ' (' + ogCode + ') | CMonJardinier',
      description: 'Trouvez les meilleurs jardiniers paysagistes à ' + ogCity + ' (' + ogCode + '). Devis gratuit et crédit d\'impôt 50%.',
      url: 'https://www.cmonjardinier.com/app/jardiniers/' + encodeURIComponent(ogCity) + '/' + ogCode,
      image: ogPic
    })
  }

  // New method to handle sponsor code
  async handleSponsorCode (sponsorCode) {
    debug(`[GardenersPage] Processing sponsor code: ${sponsorCode}`)
    
    stores.inquiry.clear()
    await initialization

    await stores.search.setQuery({ code1: sponsorCode })

    if (!stores.search.gardeners.length) {
      debug(`[GardenersPage] No gardeners found for sponsor code: ${sponsorCode}`)
      return
    }

    if (stores.authentication.isAuthententicated()) {
      stores.inquiry.setAccount({ account: stores.account.connected })
    }

    stores.search.fromJardiniers = true
    stores.search.setEntryFunnel(LeadFunnels.SITE_CARTE)
    stores.search.selectAll()

    await this.injectSponsorSEO(sponsorCode)
  }

  // SEO for sponsor code
  async injectSponsorSEO (sponsorCode) {
    try {
      const metadata = {
        title: `Jardiniers paysagistes - Code parrainage ${sponsorCode} | CMonJardinier`,
        description: `Découvrez nos jardiniers paysagistes avec le code parrainage ${sponsorCode}. Devis gratuit et réduction fiscale de 50%.`,
        keywords: `jardinier, paysagiste, devis jardinage, code parrainage, ${sponsorCode}`,
        canonical: `${PagesPaths.GARDENERS.path}/${sponsorCode}`
      }

      const context = {
        sponsorCode: sponsorCode,
        url: window.location.pathname
      }

      stores.metadata.setValues(metadata, context)

      const injectionResult = injectSEOMetadata(metadata, context)

      if (injectionResult.errors.length > 0) {
        console.error('[GardenersPage] Sponsor SEO injection errors:', injectionResult.errors)
      }

      updateCanonicalLink(`${PagesPaths.GARDENERS.path}/${sponsorCode}`)
      
      debug(`[GardenersPage] Sponsor SEO injected for: ${sponsorCode}`)
    } catch (error) {
      console.error('[GardenersPage] Sponsor SEO injection failed:', error)
    }
  }

  async handlePageWithPostalCode (city, code) {
    const { history } = this.props

    // Postal code format validation
    if (!/^\d{5}$/.test(code)) {
      debug(`[GardenersPage] Invalid postal code format: ${code}`)
      history.replace('/')
      return
    }

    // Reset store before search to avoid stale cache from a previous session
    // (if the external city/postalCode API fails, we don't want to keep old data)
    const urlNormalizedCode = code
    const storedCode = stores.search.search.postalCode
    const storedCity = stores.search.search.city
    const storedCityNormalized = storedCity ? this.urlFriendlyCity(storedCity) : null

    if (storedCode !== urlNormalizedCode || storedCityNormalized !== city) {
      debug(`[GardenersPage] Stale cache detected, resetting store (was ${storedCity}/${storedCode}, now ${city}/${urlNormalizedCode})`)
      stores.search.gardeners = []
      stores.search.search.postalCode = null
      stores.search.search.city = null
      stores.search.search.allPostalCodesOfCity = null
    }

    // Search with cross-validation city/postal code
    await stores.search.setQuery({
      code1: city,
      code2: code
    })

    // Validation after setQuery
    if (stores.search.search.city && stores.search.search.postalCode) {
      const expectedCity = this.urlFriendlyCity(stores.search.search.city)
      const expectedCode = stores.search.search.postalCode

      debug(
        `[GardenersPage] Comparing: "${city}" vs "${expectedCity}", "${code}" vs "${expectedCode}"`
      )

      const urlFriendlyNormalizedCity = this.urlFriendlyCity(city)

      if (urlFriendlyNormalizedCity !== expectedCity || code !== expectedCode) {
        const correctUrl = `${PagesPaths.GARDENERS.path}/${expectedCity}/${expectedCode}`
        debug(`[GardenersPage] Redirecting to canonical URL: ${correctUrl}`)
        history.replace(correctUrl)
        return
      }

      // Canonical to itself for page with zip code
      updateCanonicalLink(`${PagesPaths.GARDENERS.path}/${city}/${code}`)

      // If no gardeners cover this postal code, mark as noindex to avoid thin content
      if (!stores.search.gardeners.length) {
        addNoIndexIfNotExists()
        debug(`[GardenersPage] No gardeners for ${code}, adding noindex`)
      } else {
        removeNoIndexIfExists()
      }
    } else if (!stores.search.gardeners.length) {
      // City/postal code validation failed AND no results — add noindex
      addNoIndexIfNotExists()
      debug('[GardenersPage] No gardeners found for specific postal code, adding noindex')
    }
  }

  async handlePageWithoutPostalCode (city) {
    const { history } = this.props

    // Mark the page as noindex immediately — bots crawling /jardiniers/{city}
    // without a postal code should never index this intermediate URL
    addNoIndexIfNotExists()

    // Resolve city to get its postal codes
    await stores.search.setQuery({ code1: city })

    if (!stores.search.search.city) {
      debug('[GardenersPage] City not found, redirecting to home')
      history.replace('/')
      return
    }

    // Get the first postal code to redirect to
    const expectedCity = this.urlFriendlyCity(stores.search.search.city)
    const allPostalCodes = stores.search.search.allPostalCodesOfCity || []
    const firstPostalCode =
      allPostalCodes.length > 0
        ? allPostalCodes[0]
        : stores.search.search.postalCode

    if (firstPostalCode) {
      // Set canonical to the final URL so Prerender-captured HTML still tells Google
      // where the real page lives, even if the JS redirect happens later
      const redirectUrl = `${PagesPaths.GARDENERS.path}/${expectedCity}/${firstPostalCode}`
      updateCanonicalLink(redirectUrl)
      debug(`[GardenersPage] Redirecting city-only URL to: ${redirectUrl}`)
      history.replace(redirectUrl)
    } else {
      debug('[GardenersPage] No postal code found for city, redirecting to home')
      history.replace('/')
    }
  }

  normalizeCity (city) {
    return decodeURIComponent(city)
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }

  urlFriendlyCity (city) {
    return city
      .replace(/\s+/g, '-')
      .replace(/['"]/g, '')
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ç]/g, 'c')
      .replace(/[ñ]/g, 'n')
  }

  async updateSelectedPostalCode (postalCode) {
    if (!postalCode) {
      debug('[GardenersPage] Empty postal code ignored — "tous" is no longer supported')
      return
    }

    const { history } = this.props
    const currentCity = stores.search.search.city
    const urlFriendlyCity = this.urlFriendlyCity(currentCity)

    debug(`[GardenersPage] Switching to specific postal code: ${postalCode}`)

    // 1. Reload data for specific postal code
    await stores.search.setQuery({ code1: postalCode, code2: currentCity })

    // 2. Change URL
    const specificUrl = `${PagesPaths.GARDENERS.path}/${urlFriendlyCity}/${postalCode}`
    history.replace(specificUrl)

    // 3. Canonical to itself
    updateCanonicalLink(specificUrl)

    // 4. Handle noindex if no gardeners for this postal code
    if (!stores.search.gardeners.length) {
      addNoIndexIfNotExists()
    } else {
      removeNoIndexIfExists()
    }

    // 5. Update metadata
    this.updateMetaTags()
  }

  componentWillUnmount () {
    removeCanonicalLink()
    removeNoIndexIfExists()
    removeJsonLd()
    removeOpenGraph()
  }

  /**
   * Optimized SEO injection with fallbacks
   */
  async injectOptimizedSEO () {
    try {
      // Metadata update via the store
      this.updateMetaTags()

      // Wait for the blind to be updated
      await new Promise(function (resolve) {
        setTimeout(resolve, 100)
      })

      // Direct injection with validation — every page now has a postal code
      const canonicalPath = this.props.match.params.code
        ? `${PagesPaths.GARDENERS.path}/${this.props.match.params.city}/${this.props.match.params.code}`
        : null

      const metadata = {
        title: stores.metadata.values.title,
        description: stores.metadata.values.description,
        keywords: stores.metadata.values.keywords,
        canonical: canonicalPath
      }

      const context = {
        city: stores.search.search.city,
        postalCode: stores.search.search.postalCode,
        url: window.location.pathname
      }

      const injectionResult = injectSEOMetadata(metadata, context)

      if (injectionResult.errors.length > 0) {
        console.error('[GardenersPage] SEO injection errors:', injectionResult.errors)

        // Fallback on failure
        this.injectFallbackSEO(context)
      }
    } catch (error) {
      console.error('[GardenersPage] SEO injection failed:', error)
    }
  }

  /**
   * SEO fallbacks in case of failure
   */
  injectFallbackSEO (context) {
    const fallbackMetadata = {
      title: `Jardiniers paysagistes à ${context.city} (${context.postalCode}) | CMonJardinier`,
      description: `Trouvez les meilleurs jardiniers paysagistes à ${context.city} (${context.postalCode}). Devis gratuit et réduction fiscale de 50%.`,
      keywords: `${context.city}, ${context.postalCode}, jardinier, paysagiste, devis jardinage`
    }

    injectSEOMetadata(fallbackMetadata, context)
    debug('[GardenersPage] Fallback SEO injected')
  }

  updateMetaTags () {
    const { t, match } = this.props

    // For sponsor code, we have simplified metadata
    if (stores.search.search.sponsor) {
      const sponsorCode = stores.search.search.sponsor
      const metadata = {
        logo: `Jardiniers paysagistes - Code parrainage ${sponsorCode}`,
        alt: `Jardiniers paysagistes - Code parrainage ${sponsorCode}`,
        title: `Jardiniers paysagistes - Code parrainage ${sponsorCode} | CMonJardinier`,
        description: `Découvrez nos jardiniers paysagistes avec le code parrainage ${sponsorCode}. Devis gratuit et réduction fiscale de 50%.`,
        keywords: `jardinier, paysagiste, devis jardinage, code parrainage, ${sponsorCode}`
      }

      stores.metadata.setValues(metadata, {
        sponsorCode: sponsorCode,
        url: window.location.pathname
      })

      debug(`[GardenersPage] Meta tags set for sponsor code: ${sponsorCode}`)
      return
    }

    // Data validation before generation
    if (!stores.search.search.city) {
      console.error('[GardenersPage] Missing city for meta generation')
      return
    }

    // Every page must have a postal code (city-only URLs redirect in handlePageWithoutPostalCode)
    if (!stores.search.search.postalCode) {
      console.error('[GardenersPage] Missing postal code for meta generation')
      return
    }

    var rawTitle = stores.search.metaTitle
      ? stores.search.metaTitle
      : t('common:seo:jardinier:title', stores.search.search)
    const metaTitle = rawTitle && rawTitle.indexOf('CMonJardinier') === -1 && rawTitle.indexOf('Cmonjardinier') === -1
      ? rawTitle + ' | CMonJardinier'
      : rawTitle

    const metaDescription = stores.search.metaDescription
      ? stores.search.metaDescription
      : t('common:seo:jardinier:description', stores.search.search)

    stores.metadata.setValues(
      {
        logo: t('common:seo:jardinier:logo', stores.search.search),
        alt: t('common:seo:jardinier:alt', stores.search.search),
        title: metaTitle,
        description: metaDescription,
        keywords: t('common:seo:jardinier:keywords', stores.search.search)
      },
      {
        city: stores.search.search.city,
        postalCode: stores.search.search.postalCode,
        canonical: `${PagesPaths.GARDENERS.path}/${match.params.city}/${match.params.code}`,
        url: window.location.pathname
      }
    )
  }

  get breadcrumbs () {
    if (stores.search.search.sponsor) {
      return [
        {
          name: this.props.t('common:jardiniers:gardeners_sponsor', {
            sponsor: stores.search.search.sponsor
          })
        }
      ]
    }

    const allPostalCodes = stores.search.search.allPostalCodesOfCity || []
    const hasMultiplePostalCodes = allPostalCodes.length > 1

    if (stores.search.search.postalCode && hasMultiplePostalCodes) {
      // Multiple CP for this city: show "Jardiniers à Amiens > 80000" (city not clickable)
      return [
        {
          name: this.props.t('common:jardiniers:gardeners_at', {
            city: stores.search.search.city
          })
        },
        {
          name: stores.search.search.postalCode
        }
      ]
    }

    // Single CP or no CP: just show "Jardiniers à Amiens"
    return [
      {
        name: this.props.t('common:jardiniers:gardeners_at', {
          city: stores.search.search.city
        })
      }
    ]
  }

  render () {
    if (stores.search.loading) {
      return <Loader />
    }

    if (!stores.search.gardeners.length) {
      return (
        <NoGardeners
          postalCode={stores.search.search.postalCode}
          referralCode={stores.search.search.sponsor}
          onPostalCodeChange={(code) => this.updateSelectedPostalCode(code)}
        />
      )
    }

    return (
      <div>
        <SubHero breadcrumb={this.breadcrumbs} />
        <div className='--with-padding-bottom'>
          <Gardeners
            withInfoText
            onPostalCodeChange={(code) => this.updateSelectedPostalCode(code)}
            city={stores.search.search.city}
            selected={stores.search.selected}
            jardiniers={stores.search.gardeners}
            cityDescriptionHtml={stores.search.cityDescriptionHtml}
            titleHtml={stores.search.titleHtml}
            metaDescription={stores.search.metaDescription}
            metaTitle={stores.search.metaTitle}
            pictureList={stores.search.pictureList}
          />
        </div>
      </div>
    )
  }
}

GardenersPageComponent.propTypes = {
  t: func,
  match: shape({
    params: shape({
      code: string
    })
  }),
  history: object
}

export const GardenersPage = withRouter(
  translate()(view(GardenersPageComponent))
)


// WEBPACK FOOTER //
// ./src/pages/jardiniers/GardenersPage.js